import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Eye, Copy, Trash, Globe, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const NamecheapProDashboard = () => {
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purgingId, setPurgingId] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap-pro/deployments`, { withCredentials: true });
      setDeployments(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handlePurge = async (id) => {
    if (!window.confirm("Restart Heroku dynos to pull fresh Git code?")) return;
    setPurgingId(id);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap-pro/deployments/${id}/purge`, {}, { withCredentials: true });
      toast.success("Purge initiated! Heroku dynos restarting.");
    } catch (err) {
      toast.error("Failed to initiate purge");
    } finally {
      setPurgingId(null);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black mb-2 text-[#00E5FF]">NAMECHEAP PRO</h1>
        <p className="text-slate-400 font-mono">Manage Namecheap Domains linked directly to Heroku.</p>
      </div>

      <div className="border border-white/10 bg-card overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 bg-black/20">
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Date</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Custom Domain</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Heroku Account</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Heroku Link</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Live URL</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {deployments.map(dep => (
              <tr key={dep.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="p-4 text-sm text-slate-400 font-mono">{format(new Date(dep.created_at), 'MMM dd, HH:mm')}</td>
                <td className="p-4 font-mono text-sm font-bold text-white flex items-center gap-2">
                  <Globe className="text-[#00E5FF]" size={18} />
                  {dep.domain}
                </td>
                <td className="p-4 text-sm text-slate-400">{dep.heroku_label}</td>
                <td className="p-4 text-sm">
                  {dep.heroku_url ? (
                    <div className="flex items-center gap-2">
                      <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-[#8b60ed] font-mono truncate w-48 select-all" title={dep.heroku_url}>
                        {dep.heroku_url}
                      </div>
                      <button onClick={() => { navigator.clipboard.writeText(dep.heroku_url); toast.success("Copied Heroku URL!"); }} className="text-slate-400 hover:text-white transition-colors p-1"><Copy size={16} /></button>
                      <a href={dep.heroku_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#8b60ed] transition-colors p-1"><Eye size={16} /></a>
                    </div>
                  ) : '-'}
                </td>
                <td className="p-4 text-sm">
                  <span className={`px-2 py-1 text-xs font-bold tracking-wider ${
                    dep.status === 'live' ? 'bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/20' : 
                    dep.status === 'failed' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 
                    'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20'
                  }`}>
                    {dep.status}
                  </span>
                </td>
                <td className="p-4 text-sm">
                  {dep.public_url ? (
                    <div className="flex items-center gap-2">
                      <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-slate-400 font-mono truncate w-48 select-all" title={dep.public_url}>
                        {dep.public_url}
                      </div>
                      <button onClick={() => { navigator.clipboard.writeText(dep.public_url); toast.success("Copied!"); }} className="text-slate-400 hover:text-white transition-colors p-1"><Copy size={16} /></button>
                      <a href={dep.public_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#00E5FF] transition-colors p-1"><Eye size={16} /></a>
                    </div>
                  ) : dep.status === 'failed' ? (
                    <span className="text-red-500 text-xs truncate max-w-[200px] block" title={dep.error_msg}>{dep.error_msg}</span>
                  ) : '-'}
                </td>
                <td className="p-4 flex items-center justify-end gap-2">
                  {dep.status === 'live' && (
                    <button 
                      onClick={() => handlePurge(dep.id)}
                      disabled={purgingId === dep.id}
                      className="bg-transparent text-slate-400 border border-white/10 hover:text-white hover:border-white/30 transition-colors px-3 py-1 font-bold text-xs inline-flex items-center gap-1 uppercase tracking-wider disabled:opacity-50"
                      title="Restart Heroku Dynos"
                    >
                      <ArrowsClockwise size={14} className={purgingId === dep.id ? "animate-spin" : ""} /> Purge
                    </button>
                  )}
                  <button 
                    onClick={async () => {
                      if (!window.confirm("Are you sure? This will delete the deployment and Heroku app.")) return;
                      try {
                        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/namecheap-pro/deployments/${dep.id}`, { withCredentials: true });
                        toast.success("Deleted");
                        fetchHistory();
                      } catch (e) {
                        toast.error("Failed to delete");
                      }
                    }}
                    className="bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white transition-colors px-3 py-1 font-bold text-xs inline-flex items-center gap-1 uppercase tracking-wider"
                  >
                    <Trash size={14} /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {!loading && deployments.length === 0 && (
              <tr><td colSpan="7" className="p-8 text-center text-slate-500">No Custom Domains Deployed.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NamecheapProDashboard;